<template>
  <!-- Field input -->
  <div>
    <q-select
      clearable
      filled
      color="accent"
      v-model="conditional.field"
      :options="fieldsAsArray"
      behavior="menu"
      :option-label="conditionLabel"
      :label="t('data.filtering.field')"
      @update:model-value="newField(), conditionStackChange()"
    >
    </q-select>
  </div>

  <!-- Operator Input -->
  <div>
    <q-select
      clearable
      filled
      color="accent"
      v-model="conditional.operation"
      :options="getOperators(conditional)"
      behavior="menu"
      :option-label="operatorLabel"
      :label="t('data.filtering.operator')"
      @update:model-value="conditionStackChange()"
    >
    </q-select>
  </div>
  <!-- Value input -->
  <!-- Dates -->
  <template v-if="isDate(conditional)">
    <div>
      <!-- Date selection -->
      <q-select
        clearable
        filled
        color="accent"
        v-if="isDate(conditional)"
        debounce="400"
        :options="getDateModifiers()"
        behavior="menu"
        :option-label="dateLabel"
        :label="t('data.filtering.date')"
        v-model="conditional.dateModifier"
        @update:model-value="conditionStackChange()"
      ></q-select>
    </div>
    <div v-show="conditional.dateModifier != null && conditional.dateModifier.input">
      <!-- Date input -->
      <q-input
        color="accent"
        filled
        v-if="
          conditional.field === null ||
          (isDate(conditional) &&
            conditional.dateModifier != null &&
            conditional.dateModifier.input)
        "
        debounce="400"
        :placeholder="t('data.filtering.value')"
        v-model="conditional.value"
        type="date"
        @update:model-value="conditionStackChange()"
      >
        <!-- <template #append>
          <q-icon
            name="close"
            @click.stop=";(conditional.value = null), conditionStackChange()"
            class="cursor-pointer"
          />
        </template> -->
      </q-input>
    </div>
  </template>
  <!-- Number/Duration -->
  <template v-else-if="isNumber(conditional)">
    <div>
      <q-input
        color="accent"
        filled
        type="number"
        v-if="
          conditional.field === null || (!isDropdown(conditional) && !isEmptyField(conditional))
        "
        debounce="400"
        :label="t('data.filtering.value')"
        v-model.number="conditional.value"
        @keyup.enter="enterKey()"
        @keypress="inputValidation($event, conditional)"
        @update:model-value="conditionStackChange()"
      >
        <template #append>
          <q-icon
            name="close"
            @click.stop=";(conditional.value = null), conditionStackChange()"
            class="cursor-pointer"
          />
        </template>
      </q-input>
    </div>
  </template>
  <!-- Non Date inputs -->
  <template v-else>
    <div>
      <!-- Text/Number input -->
      <q-input
        color="accent"
        filled
        v-if="
          conditional.field === null || (!isDropdown(conditional) && !isEmptyField(conditional))
        "
        debounce="400"
        :label="t('data.filtering.value')"
        v-model="conditional.value"
        @keyup.enter="enterKey()"
        @keypress="inputValidation($event, conditional)"
        @update:model-value="conditionStackChange()"
      >
        <template #append>
          <q-icon
            name="close"
            @click.stop=";(conditional.value = null), conditionStackChange()"
            class="cursor-pointer"
          />
        </template>
      </q-input>
      <!-- Dropdown -->
      <q-select
        clearable
        filled
        multiple
        color="accent"
        v-else-if="isDropdown(conditional) && !isEmptyField(conditional)"
        v-show="isDropdown(conditional) && !isEmptyField(conditional)"
        v-model="conditional.value"
        :options="options"
        behavior="menu"
        :label="t('data.filtering.value')"
        @update:model-value="conditionStackChange()"
      >
        <template #append>
          <q-icon
            name="close"
            @click.stop=";(conditional.value = null), conditionStackChange()"
            class="cursor-pointer"
          />
        </template>
      </q-select>
    </div>
  </template>
</template>
<script lang="ts">
import { useI18n } from 'vue-i18n'
import { LanguageChoice } from '../i18n'
import { computed, defineComponent, onBeforeMount, PropType, ref, Ref } from 'vue'
import { Condition, Fields, useConditionals } from '../composables/conditionals'
import { ColumnsMeta } from 'app/types/components/data-rendering'
import { isNullish, isPropertyOfObjectNullish } from '../utils'
export default defineComponent({
  emits: ['conditionalChange', 'enterKey'],
  props: {
    fieldsAsArray: {
      required: true,
      type: Array as PropType<Fields>,
    },
    fields: {
      required: true,
      type: Object as PropType<ColumnsMeta>,
    },
    inputs: {
      required: false,
      type: Object as PropType<Condition>,
      default: () => {
        return { field: null, operator: null, value: null }
      },
    },
  },
  setup(props, { emit }) {
    const { locale, t } = useI18n({ useScope: 'global' })
    const localeString = computed(() => locale.value as LanguageChoice)
    const { getOperatorsByType, getDateModifiers } = useConditionals()
    const getOperators = (conditional: Condition) => {
      if (conditional.options) {
        return getOperatorsByType('array')
      }

      return getOperatorsByType(conditional.field ? conditional.field.type : null)
    }
    const initialOperators = ref(getOperatorsByType(props.fieldsAsArray[0].type))
    const conditional: Ref<Condition> = ref({
      field: props.inputs.field !== null ? props.inputs.field : props.fieldsAsArray[0],
      operation: props.inputs.operation ? props.inputs.operation : initialOperators.value[0],
      value: props.inputs.value ? props.inputs.value : null,
      dateModifier: props.inputs.dateModifier ? props.inputs.dateModifier : null,
    })
    const conditionLabel = (field: Condition['field']): string => {
      return field ? field.label[localeString.value] : ''
    }
    const operatorLabel = (operation: Condition['operation']): string => {
      return operation ? t(`data.filtering.operators.comparison.${operation.key}`) : ''
    }
    const dateLabel = (operation: Condition['dateModifier']): string => {
      return operation ? t(`data.filtering.operators.date.${operation.key}`) : ''
    }
    onBeforeMount(() => {
      if (
        !isPropertyOfObjectNullish(conditional.value, ['field', 'name']) &&
        'options' in props.fields[conditional.value.field!.name]
      ) {
        conditional.value.options = props.fields[conditional.value.field!.name].options?.map(
          (option) => option[localeString.value]
        )
        conditional.value.optionsKey = props.fields[conditional.value.field!.name].optionsKey
      }
    })
    const conditionStackChange = () => {
      if (
        !isPropertyOfObjectNullish(conditional.value, ['field', 'name']) &&
        'options' in props.fields[conditional.value.field!.name]
      ) {
        conditional.value.options = props.fields[conditional.value.field!.name].options?.map(
          (option) => option[localeString.value]
        )
        conditional.value.optionsKey = props.fields[conditional.value.field!.name].optionsKey
      } else {
        delete conditional.value.options
        delete conditional.value.optionsKey
      }
      emit('conditionalChange', conditional.value)
    }
    //Removes stale data
    const newField = () => {
      if (
        !isPropertyOfObjectNullish(conditional.value, ['field', 'name']) &&
        'options' in props.fields[conditional.value.field!.name]
      ) {
        conditional.value.options = props.fields[conditional.value.field!.name].options?.map(
          (option) => option[localeString.value]
        )
        conditional.value.optionsKey = props.fields[conditional.value.field!.name].optionsKey
      }
      const newInitialOperators = getOperators(conditional.value)
      conditional.value.operation = newInitialOperators[0]
      conditional.value.value = null
      if ('dateModifier' in conditional.value) {
        conditional.value.dateModifier = undefined
      }
    }
    const isDate = (condition: Condition): boolean => {
      if (isNullish(condition.field)) {
        return false
      }
      const field = props.fields[condition.field.name]
      if (!isNullish(field) && field.type === 'date') {
        return true
      }
      return false
    }
    const isNumber = (condition: Condition): boolean => {
      if (isNullish(condition.field)) {
        return false
      }
      const field = props.fields[condition.field.name]
      if (!isNullish(field) && (field.type === 'number' || field.type === 'time')) {
        return true
      }
      return false
    }
    const isDropdown = (condition: Condition): boolean => {
      if (
        !isPropertyOfObjectNullish(condition, ['field', 'name']) &&
        'options' in props.fields[condition.field!.name]
      ) {
        return true
      }
      return false
    }
    const isEmptyField = (condition: any): boolean => {
      if (isPropertyOfObjectNullish(condition, ['operation', 'operation'])) {
        return false
      }
      if (condition.operation.operation.length < 2) {
        return true
      }
      return false
    }
    const inputValidation = (evt: KeyboardEvent, condition: Condition): void => {
      if (isPropertyOfObjectNullish(condition, 'field')) return
      const testInteger = new RegExp('^[0-9]*$')
      if (
        !testInteger.test(evt.key) &&
        (condition.field!.type == 'number' || condition.field!.type == 'time')
      ) {
        evt.preventDefault()
      } else false
    }
    const inputRules = (val: string, condition: Condition) => {
      if (isPropertyOfObjectNullish(condition, 'field')) return
      const testInteger = new RegExp('^[0-9]*$')
      if (condition.field!.type == 'number' || condition.field!.type == 'time') {
        if (testInteger.test(val)) {
          return true
        } else {
          return 'Input must be a number'
        }
      } else {
        return true
      }
    }
    const enterKey = () => {
      emit('enterKey')
    }
    const options = computed(() => {
      if (props.fields && !isPropertyOfObjectNullish(conditional.value, 'field')) {
        return props.fields[conditional.value.field!.name].options?.map(
          (option) => option[localeString.value]
        )
      } else {
        return []
      }
    })
    return {
      getOperatorsByType,
      getOperators,
      getDateModifiers,
      conditional,
      localeString,
      conditionLabel,
      dateLabel,
      operatorLabel,
      newField,
      isDate,
      isNumber,
      isDropdown,
      isEmptyField,
      inputValidation,
      inputRules,
      conditionStackChange,
      enterKey,
      t,
      options,
    }
  },
})
</script>
